/**
 * Utilitários de cor usados pelos componentes de design system.
 * Sem dependências — os mesmos cálculos servem à Fase 2 (Tailwind/shadcn).
 */

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

export const hexToRgb = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

export const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("")}`;

/** Luminância relativa (WCAG 2.1). 0 = preto, 1 = branco. */
export const luminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

/** Mistura `hex` com `target` na proporção `amount` (0..1). */
export const mix = (hex, target, amount) => {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  if (!a || !b) return hex;
  return rgbToHex({
    r: a.r + (b.r - a.r) * amount,
    g: a.g + (b.g - a.g) * amount,
    b: a.b + (b.b - a.b) * amount,
  });
};

/** `hex` com canal alfa, pronto para CSS. */
export const alpha = (hex, a) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
};

/**
 * Devolve uma variante da cor escura o bastante para servir de texto sobre
 * fundo claro. Cores vindas do banco (status/tag/origem cadastrados pelo
 * usuário) podem ser amarelo-claro; sem isso o chip fica ilegível.
 */
/**
 * Tinta legível SOBRE `hex` usado como preenchimento.
 *
 * Serve para as cores cadastradas pelo usuário (tags, status, origem), que
 * precisam aparecer exatamente como foram configuradas: o fundo é a cor
 * escolhida e só o texto se adapta. Limiar em 0,5 de luminância relativa —
 * acima disso, texto escuro; abaixo, branco.
 */
export const contrastInk = (hex) =>
  luminance(hex) > 0.5 ? "#0F172A" : "#FFFFFF";

export const toReadableInk = (hex, { onDark = false } = {}) => {
  const l = luminance(hex);
  if (onDark) {
    // Em dark mode o risco é o oposto: cor escura demais some no fundo.
    return l < 0.25 ? mix(hex, "#FFFFFF", 0.55) : hex;
  }
  if (l > 0.55) return mix(hex, "#0F172A", 0.55);
  if (l > 0.35) return mix(hex, "#0F172A", 0.3);
  return hex;
};
